// 运营报问题的悬浮入口。挂在运营台上，每个页面都能点。
//
// 关键不是「能写字」，是**自动带上现场**：他在哪个页面、正看着哪条撮合单/对话。
// 事后光看一句「刚才那个博主的时间不对」谁也查不了，有了这些就能直接跳回去复现。
// 截图支持直接 Ctrl+V 粘贴 —— 运营截完图第一反应是粘贴，不是去找「选择文件」。
import React, { useState, useRef, useEffect } from 'react';
import { agentApi, uploadApi } from '../services/api';

export default function ReportIssue({ context }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [images, setImages] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState('');
  const fileRef = useRef(null);

  const addFiles = async (files) => {
    const list = [...files].filter((f) => f.type.startsWith('image/')).slice(0, 9 - images.length);
    if (!list.length) return;
    setBusy(true);
    try {
      const urls = [];
      for (const f of list) urls.push(await uploadApi.image(f));
      setImages((p) => [...p, ...urls]);
    } catch (e) {
      setDone('图片没传上去：' + (e?.message || ''));
    } finally { setBusy(false); }
  };

  // 截图之后直接 Ctrl+V
  useEffect(() => {
    if (!open) return;
    const onPaste = (e) => {
      const items = [...(e.clipboardData?.items || [])];
      const files = items.filter((i) => i.kind === 'file').map((i) => i.getAsFile()).filter(Boolean);
      if (files.length) { e.preventDefault(); addFiles(files); }
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  });

  const submit = async () => {
    if (!text.trim() && !images.length) { setDone('说一句哪里不对，或者贴张图'); return; }
    setBusy(true);
    try {
      await agentApi.reportIssue({
        text: text.trim(),
        images,
        page: window.location.hash || '',
        ua: navigator.userAgent,
        ...(context || {}),
      });
      setText(''); setImages([]); setDone('收到了，会有人看');
      setTimeout(() => { setOpen(false); setDone(''); }, 1400);
    } catch (e) {
      setDone(e?.response?.data?.error || '没提交上去');
    } finally { setBusy(false); }
  };

  if (!open) {
    return <div style={s.fab} onClick={() => setOpen(true)} title="报个问题">报个问题</div>;
  }

  return (
    <div style={s.mask} onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}>
      <div style={s.panel}>
        <div style={s.head}>
          <span style={s.title}>报个问题</span>
          <span style={s.close} onClick={() => setOpen(false)}>×</span>
        </div>
        <div style={s.hint}>
          写清楚「你看到什么、你以为该是什么」。你现在在哪个页面、看的哪条单子会自动带上，不用写。
        </div>
        <textarea
          style={s.ta}
          value={text}
          autoFocus
          placeholder="比如：这个博主明明回复了，行程里还是「待确认」"
          onChange={(e) => setText(e.target.value)}
        />
        <div style={s.imgRow}>
          {images.map((u) => (
            <div key={u} style={s.thumbWrap}>
              <img src={u} alt="" style={s.thumb} />
              <span style={s.del} onClick={() => setImages((p) => p.filter((x) => x !== u))}>×</span>
            </div>
          ))}
          {images.length < 9 && (
            <div style={s.addImg} onClick={() => fileRef.current?.click()}>+ 截图<br /><span style={s.addSub}>或 Ctrl+V</span></div>
          )}
          <input ref={fileRef} type="file" accept="image/*" multiple style={{ display: 'none' }}
            onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
        </div>
        {done && <div style={s.done}>{done}</div>}
        <button style={{ ...s.send, opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={submit}>
          {busy ? '提交中…' : '提交'}
        </button>
      </div>
    </div>
  );
}

const s = {
  fab: {
    position: 'fixed', right: 20, bottom: 24, zIndex: 60,
    background: '#18181b', color: '#fff', fontSize: 13,
    borderRadius: 999, padding: '10px 18px', cursor: 'pointer',
    boxShadow: '0 6px 20px rgba(0,0,0,.22)',
  },
  mask: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.35)', zIndex: 70, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' },
  panel: { background: '#fff', width: '100%', maxWidth: 560, borderRadius: '16px 16px 0 0', padding: 20, boxSizing: 'border-box' },
  head: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  title: { fontSize: 16, fontWeight: 600, color: '#111' },
  close: { fontSize: 26, color: '#9ca3af', cursor: 'pointer', lineHeight: 1 },
  hint: { fontSize: 12, color: '#9ca3af', lineHeight: 1.7, margin: '8px 0 12px' },
  ta: { width: '100%', minHeight: 90, padding: 12, border: '1.5px solid #e5e7eb', borderRadius: 10, fontSize: 14, outline: 'none', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' },
  imgRow: { display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 },
  thumbWrap: { position: 'relative', width: 72, height: 72 },
  thumb: { width: 72, height: 72, objectFit: 'cover', borderRadius: 8, border: '1px solid #e5e7eb' },
  del: { position: 'absolute', top: -6, right: -6, width: 20, height: 20, lineHeight: '18px', textAlign: 'center', borderRadius: '50%', background: '#18181b', color: '#fff', fontSize: 14, cursor: 'pointer' },
  addImg: { width: 72, height: 72, border: '1.5px dashed #d1d5db', borderRadius: 8, color: '#9ca3af', fontSize: 12, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', textAlign: 'center', lineHeight: 1.5 },
  addSub: { fontSize: 10, color: '#c4c4cc' },
  done: { fontSize: 13, color: '#FA1C51', marginTop: 10 },
  send: { width: '100%', marginTop: 14, height: 42, border: 'none', borderRadius: 999, background: '#FA1C51', color: '#fff', fontSize: 15, fontWeight: 600, cursor: 'pointer' },
};
